import React, { Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './stores/auth';
const AuthPage = React.lazy(() => import('./pages/AuthPage').then(m => ({ default: m.AuthPage })));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const PlanningSessionPage = React.lazy(() => import('./pages/PlanningSessionPage').then(m => ({ default: m.PlanningSessionPage })));
const CreateSessionPage = React.lazy(() => import('./pages/CreateSessionPage').then(m => ({ default: m.CreateSessionPage })));
const GuestJoinPage = React.lazy(() => import('./pages/GuestJoinPage').then(m => ({ default: m.JoinSessionPage })));
import { LoadingSpinner } from './components/ui/LoadingSpinner';
const HomePage = React.lazy(() => import('./pages/HomePage').then(m => ({ default: m.HomePage })));
const ShufflePage = React.lazy(() => import('./pages/ShufflePage').then(m => ({ default: m.ShufflePage })));
const QuickStartPage = React.lazy(() => import('./pages/QuickStartPage').then(m => ({ default: m.QuickStartPage })));
const WorldClockPage = React.lazy(() => import('./pages/WorldClockPage').then(m => ({ default: m.WorldClockPage })));
const StopwatchPage = React.lazy(() => import('./pages/StopwatchPage').then(m => ({ default: m.StopwatchPage })));
const CalendarPage = React.lazy(() => import('./pages/CalendarPage').then(m => ({ default: m.CalendarPage })));
const AlarmPage = React.lazy(() => import('./pages/AlarmPage').then(m => ({ default: m.AlarmPage })));
const CalculatorPage = React.lazy(() => import('./pages/CalculatorPage').then(m => ({ default: m.CalculatorPage })));
const PlanningPokerLandingPage = React.lazy(() => import('./pages/landing/PlanningPokerLandingPage').then(m => ({ default: m.PlanningPokerLandingPage })));
const TeamVotingLandingPage = React.lazy(() => import('./pages/landing/TeamVotingLandingPage').then(m => ({ default: m.TeamVotingLandingPage })));
const WorldClockLandingPage = React.lazy(() => import('./pages/landing/WorldClockLandingPage').then(m => ({ default: m.WorldClockLandingPage })));
const StopwatchLandingPage = React.lazy(() => import('./pages/landing/StopwatchLandingPage').then(m => ({ default: m.StopwatchLandingPage })));
const CalendarLandingPage = React.lazy(() => import('./pages/landing/CalendarLandingPage').then(m => ({ default: m.CalendarLandingPage })));
const AlarmLandingPage = React.lazy(() => import('./pages/landing/AlarmLandingPage').then(m => ({ default: m.AlarmLandingPage })));
const ShuffleToolLandingPage = React.lazy(() => import('./pages/landing/ShuffleToolLandingPage').then(m => ({ default: m.ShuffleToolLandingPage })));
const CalculatorFeatureLandingPage = React.lazy(() => import('./pages/landing/CalculatorFeatureLandingPage').then(m => ({ default: m.CalculatorFeatureLandingPage })));
const PlanningPokerVariationLandingPage = React.lazy(() => import('./pages/landing/PlanningPokerVariationLandingPage').then(m => ({ default: m.PlanningPokerVariationLandingPage })));
import { motion } from 'framer-motion';
const BillSufflePage = React.lazy(() => import('./pages/BillSufflePage').then(m => ({ default: m.BillSufflePage })));
import { Waves } from 'lucide-react';
const TimingInfoComponent = React.lazy(() => import('./components/timing/TimingInfoComponent').then(m => ({ default: m.default })));
import { useFeedbackPrompt } from './hooks/useFeedbackPrompt';
import { FeedbackModal } from './components/feedback/FeedbackModal';

// New organization and subscription pages
const CreateOrganizationPage = React.lazy(() => import('./pages/CreateOrganizationPage').then(m => ({ default: m.CreateOrganizationPage })));
const SubscriptionPage = React.lazy(() => import('./pages/SubscriptionPage').then(m => ({ default: m.SubscriptionPage })));
const SupportTicketsPage = React.lazy(() => import('./pages/SupportTicketsPage').then(m => ({ default: m.SupportTicketsPage })));
const ContactSalesPage = React.lazy(() => import('./pages/ContactSalesPage').then(m => ({ default: m.ContactSalesPage })));
const CreateSupportTicketPage = React.lazy(() => import('./pages/CreateSupportTicketPage').then(m => ({ default: m.CreateSupportTicketPage })));

// Custom Route Guards
import { ProtectedRoute } from './components/protect/ProtectedRoute'; // Assuming ProtectedRoute exists
import { RoleBasedRoute } from './components/protect/RoleBasedRoute';
import { UserRole } from './types/enums'; // Import UserRole enum

// New Admin/SuperAdmin pages
const SuperAdminDashboard = React.lazy(() => import('./components/superadmin/SuperAdminDashboard').then(m => ({ default: m.default })));
const OrganizationDashboard = React.lazy(() => import('./components/admin/OrganizationDashboard').then(m => ({ default: m.default })));
const SuperAdminOrganizationsPage = React.lazy(() => import('./pages/SuperAdminOrganizationsPage').then(m => ({ default: m.default })));
const SuperAdminUserManagementPage = React.lazy(() => import('./pages/SuperAdminUserManagementPage').then(m => ({ default: m.default })));
const AdminSubscriptionPlansPage = React.lazy(() => import('./pages/AdminSubscriptionPlansPage').then(m => ({ default: m.default })));
const UserManagement = React.lazy(() => import('./components/admin/UserManagement').then(m => ({ default: m.default })));


const queryClient = new QueryClient();

function App() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, initializeAuth, logout } = useAuthStore();
  const { showFeedbackModal, setShowFeedbackModal, handleFeedbackSubmitted } =
    useFeedbackPrompt();

  // 🌊 State to show/hide Threads
  const [showThreads, setShowThreads] = useState(true);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <div className="relative min-h-screen overflow-hidden">
        {/* 🌈 Threads background */}
        {/* {showThreads && (
        <div className="fixed inset-0 z-30 pointer-events-none opacity-40">
          <Threads
            color={[0.6, 0.4, 0.9]}
            amplitude={0.8}
            distance={0.1}
            enableMouseInteraction={true}
          />
        </div>
      )} */}

        {/* 🧭 Main routes */}
        <div className="relative z-10">
          <Suspense
            fallback={
              <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <LoadingSpinner size="lg" />
              </div>
            }
          >
            <Routes>
              {/* Public Routes */}
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/join/:sessionId?" element={<GuestJoinPage />} /> {/* Guest join path */}
              <Route path="/info" element={<TimingInfoComponent />} />
              <Route path="/home" element={<HomePage />} />
              <Route path="/" element={<HomePage />} /> {/* Default route for all users */}
              <Route path="/planning-poker" element={<PlanningPokerLandingPage />} />
              <Route path="/team-voting" element={<TeamVotingLandingPage />} />
              <Route path="/world-clock" element={<WorldClockLandingPage />} />
              <Route path="/stopwatch" element={<StopwatchLandingPage />} />
              <Route path="/calendar" element={<CalendarLandingPage />} />
              <Route path="/alarm" element={<AlarmLandingPage />} />
              <Route path="/shuffle/:tool" element={<ShuffleToolLandingPage />} />
              <Route path="/calculator/:feature" element={<CalculatorFeatureLandingPage />} />
              <Route path="/planning-poker/:variation" element={<PlanningPokerVariationLandingPage />} />
              <Route path="/session/:sessionId" element={<PlanningSessionPage />} />

              {/* Protected Routes - General User/Host */}
              <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
              <Route path="/create-session" element={<ProtectedRoute><CreateSessionPage /></ProtectedRoute>} />
              <Route path="/support/create" element={<ProtectedRoute><CreateSupportTicketPage /></ProtectedRoute>} />
              <Route path="/app/world-clock" element={<WorldClockPage />} />
              <Route path="/app/stopwatch" element={<StopwatchPage />} />
              <Route path="/app/calendar" element={<CalendarPage />} />
              <Route path="/app/alarm" element={<AlarmPage />} />
              <Route path="/calculator" element={<CalculatorPage />} />
              <Route path="/shuffle" element={<ShufflePage />} />
              <Route path="/billsuffle" element={<BillSufflePage />} />
              <Route path="/quick-start" element={<ProtectedRoute><QuickStartPage /></ProtectedRoute>} />
              <Route path="/contact-sales" element={<ContactSalesPage />} />


              {/* Admin Dashboard Routes */}
              <Route path="/organization-dashboard" element={<RoleBasedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}><OrganizationDashboard /></RoleBasedRoute>} />
              <Route path="/organization-dashboard/users" element={<RoleBasedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}><UserManagement /></RoleBasedRoute>} />
              <Route path="/subscription" element={<RoleBasedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}><SubscriptionPage /></RoleBasedRoute>} />
              <Route path="/support/tickets" element={<RoleBasedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}><SupportTicketsPage /></RoleBasedRoute>} />
              <Route path="/dashboard/organizations/create" element={<RoleBasedRoute allowedRoles={[UserRole.SUPER_ADMIN]}><CreateOrganizationPage /></RoleBasedRoute>} />


              {/* SuperAdmin Specific Routes */}
              <Route path="/super-admin-dashboard" element={<RoleBasedRoute allowedRoles={[UserRole.SUPER_ADMIN]}><SuperAdminDashboard /></RoleBasedRoute>} />
              <Route path="/super-admin-dashboard/organizations" element={<RoleBasedRoute allowedRoles={[UserRole.SUPER_ADMIN]}><SuperAdminOrganizationsPage /></RoleBasedRoute>} />
              <Route path="/super-admin-dashboard/users" element={<RoleBasedRoute allowedRoles={[UserRole.SUPER_ADMIN]}><SuperAdminUserManagementPage /></RoleBasedRoute>} />
              <Route path="/super-admin-dashboard/subscription-plans" element={<RoleBasedRoute allowedRoles={[UserRole.SUPER_ADMIN]}><AdminSubscriptionPlansPage /></RoleBasedRoute>} />

              {/* Fallback - Redirect authenticated users to dashboard, unauthenticated to home */}
              <Route path="*" element={<Navigate to={isAuthenticated ? "/dashboard" : "/"} replace />} />
            </Routes>
          </Suspense>
        </div>

        {/* 🌗 Responsive Toggle Button */}
        {/* 🌗 Draggable Toggle Button (Left side) */}
        <motion.button
          drag
          dragMomentum={false}
          dragElastic={0.2}
          whileTap={{ scale: 0.9 }}
          initial={{ x: 0, y: 0 }}
          dragConstraints={{ top: -500, bottom: 500, left: -50, right: 50 }}
          onClick={() => setShowThreads((prev) => !prev)}
          className="
    fixed bottom-4 left-4 z-50
    p-3 sm:p-4 rounded-full
    bg-white/80 backdrop-blur-md shadow-md
    hover:bg-white active:scale-95
    transition-all flex items-center justify-center cursor-grab active:cursor-grabbing
  "
          title={showThreads ? 'Hide Waves' : 'Show Waves'}
        >
          <Waves
            className={`w-6 h-6 sm:w-7 sm:h-7 transition-colors ${showThreads ? 'text-purple-600' : 'text-slate-400'
              }`}
          />
        </motion.button>

        {showFeedbackModal && (
          <FeedbackModal
            isOpen={showFeedbackModal}
            onClose={() => setShowFeedbackModal(false)}
            onFeedbackSubmitted={handleFeedbackSubmitted}
          />
        )}

      </div>
    </QueryClientProvider>
  );
}
export default App;
